import type { Product, SortOption } from "@/types/catalog";
import { getCategoryMeta } from "@/data/categories";
import { products } from "@/data/products";

export function getProductBySlug(slug: string): Product | undefined {
  return products.find((p) => p.slug === slug);
}

export function getProductById(id: string): Product | undefined {
  return products.find((p) => p.id === id);
}

export function getProductsByCategory(
  categorySlug: string,
  brandSlug?: string,
  options?: { includeMarketplace?: boolean },
): Product[] {
  let result = products.filter((p) => {
    if (brandSlug) {
      return p.categorySlug === categorySlug && p.brandSlug === brandSlug;
    }
    return p.categorySlug === categorySlug;
  });

  if (options?.includeMarketplace === false) {
    result = result.filter((p) => p.seller === "kmart");
  }

  return result;
}

export function searchProducts(query: string): Product[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  return products.filter(
    (p) =>
      p.title.toLowerCase().includes(q) ||
      p.tags.some((t) => t.includes(q) || q.includes(t)),
  );
}

export function sortProducts(items: Product[], sort: SortOption): Product[] {
  const sorted = [...items];
  switch (sort) {
    case "price-asc":
      return sorted.sort((a, b) => a.price - b.price);
    case "price-desc":
      return sorted.sort((a, b) => b.price - a.price);
    case "rating":
      return sorted.sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0));
    default:
      return sorted;
  }
}

export function paginateProducts<T>(items: T[], page: number, pageSize: number) {
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    items: items.slice(start, start + pageSize),
    page: safePage,
    totalPages,
    totalCount: items.length,
    rangeStart: items.length ? start + 1 : 0,
    rangeEnd: Math.min(start + pageSize, items.length),
  };
}

export function getRelatedProducts(productId: string, limit = 8): Product[] {
  const product = getProductById(productId);
  if (!product) return products.slice(0, limit);

  const related = products.filter(
    (p) =>
      p.id !== productId &&
      (p.categorySlug === product.categorySlug ||
        p.brandSlug === product.brandSlug ||
        p.tags.some((t) => product.tags.includes(t))),
  );

  return related.slice(0, limit);
}

export function getHomeTopPicks(tabId: string): Product[] {
  const map: Record<string, string> = {
    womens: "womens-clothing",
    mens: "mens-clothing",
    home: "home-living",
    toys: "toys",
  };
  const slug = map[tabId] ?? "womens-clothing";
  return getProductsByCategory(slug).slice(0, 8);
}

export { getCategoryMeta };
